import React from 'react';
import { Loader2, Eye, Wand2, Check } from 'lucide-react';
import { Template } from '../../core/template.schemas';
import { PostGenerationResult } from '../services/PostGenerationService';
import { EntitySlotSelectionMap, SelectorSelectionMap, AttributeOverrideMap } from '../types/PostGenerationTypes';
import { TemplateRenderer } from '../../components/TemplateRenderer';
import { useTemplateAssets } from '../../template-editor/hooks/useTemplateAssets';
import { useResizeObserver } from '../../template-editor/hooks/useResizeObserver';
import { SentrySectionBoundary } from '@/util-components/error/sentry-section-boundary';

interface PostPreviewSectionProps {
  /** Template for preview */
  template: Template;
  /** Generation result */
  generationResult: PostGenerationResult | null;
  /** Generated Image URL (optional) */
  generatedImageUrl?: string | null;
  /** Whether generation is in progress */
  isGenerating: boolean;
  /** Entity slots for preview */
  entitySlots: EntitySlotSelectionMap;
  /** Selector selections for preview */
  selectorSelections?: SelectorSelectionMap;
  /** Manual attribute overrides */
  manualOverrides?: AttributeOverrideMap;
  /** Current step */
  currentStep: 'analysis' | 'selection' | 'preview' | 'generation';
  /** Whether the template has dynamic content (slots/selectors) */
  hasDynamicContent?: boolean;
}

/**
 * Professional Post Preview Component
 * Compact, efficient preview with modern SaaS design
 */
export const PostPreviewSection: React.FC<PostPreviewSectionProps> = ({
  template,
  generationResult,
  generatedImageUrl,
  isGenerating,
  entitySlots,
  selectorSelections,
  manualOverrides,
  currentStep,
  hasDynamicContent = true // Default to true to maintain existing behavior if not provided
}) => {
  const rendererContainerRef = React.useRef<HTMLDivElement>(null);
  const containerSize = useResizeObserver(rendererContainerRef);
  const [lockedSize, setLockedSize] = React.useState<{ width: number; height: number } | null>(null);

  // Lock size once valid dimensions are available to prevent layout shifts during animation/loading
  React.useEffect(() => {
    if (!lockedSize && containerSize.width > 0 && containerSize.height > 0) {
      setLockedSize(containerSize);
    }
  }, [containerSize, lockedSize]);
  
  // Preload assets for the template to avoid inconsistent states
  const { loaded: assetsLoaded} = useTemplateAssets(template);
  
  const renderContent = () => {
    // Generation in progress (Initial, before image is ready)
    // Only show loader if we have dynamic content that needs processing
    // For static templates, we skip directly to the live preview while generation happens in background
    if (isGenerating && !generatedImageUrl && hasDynamicContent) {
      return (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 border border-blue-100 mx-auto mb-4">
              <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Erstelle Vorschau...</h3>
            <p className="text-xs text-gray-500">
              Bitte warten Sie einen Moment.
            </p>
          </div>
        </div>
      );
    }

    // Show generated image result if available
    if (generatedImageUrl) {
      // Calculate responsive preview size based on container
      // Use lockedSize if available (from previous step), otherwise fallback to containerSize
      // This prevents layout shifts/small images when containerSize is briefly 0 during state transitions
      const widthToUse = (lockedSize && lockedSize.width > 0) ? lockedSize.width : containerSize.width;
      const heightToUse = (lockedSize && lockedSize.height > 0) ? lockedSize.height : containerSize.height;

      const availableWidth = Math.max(widthToUse, 200); 
      const availableHeight = Math.max(heightToUse, 200);
      
      const canvasAspect = template.canvas.width / template.canvas.height;
      const containerAspect = availableWidth / availableHeight;
      
      let previewWidth, previewHeight;

      if (canvasAspect > containerAspect) {
        // Limited by width
        previewWidth = availableWidth;
        previewHeight = availableWidth / canvasAspect;
      } else {
        // Limited by height
        previewHeight = availableHeight;
        previewWidth = availableHeight * canvasAspect;
      }

      return (
        <div className="h-full flex flex-col">
          <div className="flex-none px-4 sm:px-6 py-3 sm:py-4 bg-white flex items-center gap-3 sm:gap-4">
            <div className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-lg bg-green-50 border border-green-100">
              <Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">Finales Design</h3>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Generierte Bildvorschau</p>
            </div>
          </div>
          <div ref={rendererContainerRef} className="flex-1 flex flex-col items-center justify-start p-4 overflow-hidden max-h-[500px]">
            <div 
              className="relative overflow-hidden shadow-lg rounded-lg bg-white"
              style={{ width: previewWidth, height: previewHeight }}
            >
              <img 
                src={generatedImageUrl} 
                crossOrigin="anonymous"
                alt="Generierter Post" 
                className="animate-in fade-in zoom-in-[0.99] duration-500 ease-out"
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            </div>
          </div>
        </div>
      );
    }

    // Fallback: Template preview with selected entities (Live Canvas)
    if (currentStep === 'preview' || currentStep === 'selection') {
      return (
        <div className="h-full flex flex-col">
          <div className="flex-none px-4 sm:px-6 py-3 sm:py-4 bg-white flex items-center gap-3 sm:gap-4">
            <div className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-100">
              <Eye className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">Live Vorschau</h3>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Echtzeit-Vorschau</p>
            </div>
          </div>
          <div ref={rendererContainerRef} className="flex-1 flex flex-col items-center justify-start p-8 overflow-hidden max-h-[500px] min-h-[300px]">
            {assetsLoaded && lockedSize ? (
              <SentrySectionBoundary
                boundaryKey="post-preview-template-renderer"
                title="Vorschau konnte nicht gerendert werden"
                description="Die Live-Vorschau ist abgestürzt. Du kannst diesen Bereich neu laden und weiterarbeiten."
                className="flex h-full w-full items-center justify-center p-6"
              >
                <TemplateRenderer
                  template={template}
                  entitySlots={entitySlots}
                  selectorSelections={selectorSelections}
                  manualOverrides={manualOverrides}
                  fitToContainer
                  resizeMode="contain"
                  verticalAlign="top"
                  containerSize={lockedSize}
                  showBorder={false}
                  cornerRadius={8}
                  shadow="lg"
                  className="bg-white rounded-lg overflow-hidden animate-in fade-in zoom-in-[0.99] duration-500 ease-out"
                />
              </SentrySectionBoundary>
            ) : null}
          </div>
        </div>
      );
    }

    // Analysis phase
    return (
      <div className="h-full flex items-center justify-center">
        {/* ... (Keep analysis loader if needed, but it's usually fast) ... */}
        <div className="text-center">
           <Wand2 className="h-8 w-8 text-gray-300 mx-auto mb-2" />
           <p className="text-gray-400 text-sm">Bereit zur Erstellung</p>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white w-full h-full overflow-hidden lg:min-w-[480px]">
      <div className="h-full flex flex-col">
        {renderContent()}
      </div>
    </div>
  );
};
